import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import { metricFilterSelector } from '@store/trends/base/selectors';
import { getDistributionMetrics } from '@components/metrics-panel-hz/utils/metricsMapping';
import { humanizeHoc } from '@components/metrics-panel-hz/utils/format';
import { clearMetricFilter, removeMetricFilter } from '@store/trends/base/actions';
import {
  METRIC_FILTER_TYPES,
  MF_LABEL,
} from '@components/metrics-panel-hz/utils/metricFilterUtils';
import { getMetricShortName } from '@src/utils/metrics';
import { isAppModule } from '@utils/constants';
import { Wrapper, VerticalBar, StyledTag, StyledEllipsisTooltip } from './styled';

export const buildRuleText = (metric, { lower = null, upper = null }) => {
  const distributionMetrics = getDistributionMetrics();
  // TODO: cleanup the access, using 0 now as xAXisUnit will be the same for all distributions of a metric
  const unit = distributionMetrics[metric][0].xAxisUnit;

  if (lower === null || lower === undefined) {
    return `< ${humanizeHoc(upper, unit)}`;
  }

  if (!upper) {
    return `>${isAppModule ? '=' : ''} ${humanizeHoc(lower, unit)}`;
  }

  return `${humanizeHoc(lower, unit)} - ${humanizeHoc(upper, unit)}`;
};

// boolean rule
const getBooleanRuleText = ({ metric, rule }) => {
  const shortName = getMetricShortName({ metricKey: metric });
  return `Sessions with ${shortName} = ${rule.value}`;
};

// error code rule
const getErrorCodeRuleText = ({ metric, rule }) => {
  // to be compatible with eq rule format
  const shortName = getMetricShortName({ metricKey: metric });
  const errorCodes = 'value' in rule ? [rule.value] : rule.values;
  return `${shortName}: ${errorCodes.join(', ')}`;
};

// bound rule
export const boundRuleText = ({ metric, rule }) => {
  const shortName = getMetricShortName({ metricKey: metric });
  return `${shortName}: ${buildRuleText(metric, rule)}`;
};

export const ruleTextBuilderMap = {
  [METRIC_FILTER_TYPES.error]: getErrorCodeRuleText,
  [METRIC_FILTER_TYPES.bound]: boundRuleText,
  boolean: getBooleanRuleText,
};

function MetricFilterBar() {
  const dispatch = useDispatch();
  const metricFilters = useSelector(metricFilterSelector);

  const onCloseFn = (metricFilter) => () => dispatch(removeMetricFilter(metricFilter));

  const onClear = () => {
    dispatch(clearMetricFilter());
  };

  return metricFilters.length > 0 ? (
    <Wrapper>
      <VerticalBar />
      <div style={{ width: 80, lineHeight: 1.25 }}> {`Secondary Filters [${MF_LABEL}]`} </div>
      <div style={{ flex: 1 }}>
        {metricFilters.map((metricFilter) => {
          const { metric, rule, meta } = metricFilter;

          const ruleTextBuilder = get(ruleTextBuilderMap, meta.type, boundRuleText);
          const ruleText = ruleTextBuilder({ metric, rule });

          return (
            <StyledTag
              key={`${metric}-${rule.dimension}`}
              closable
              onClose={onCloseFn(metricFilter)}
            >
              <StyledEllipsisTooltip tooltipText={ruleText}>{ruleText}</StyledEllipsisTooltip>
            </StyledTag>
          );
        })}
      </div>
      <StyledTag
        onClick={onClear}
        style={{ cursor: 'pointer', margin: '0px 5px', paddingRight: 8 }}
      >
        Clear All
      </StyledTag>
    </Wrapper>
  ) : null;
}

export default MetricFilterBar;
